import { useToast } from '@kanda-libs/ks-design-library';
import {
  services,
  useSubmit,
  type Service,
} from '@kanda-libs/ks-frontend-services';
import {
  type ContractPdfResponse,
  type ContractRequestBody,
  type ContractRequestBodyBody,
  type ContractTermsResponse,
  type PdfComponent,
} from '@kanda-libs/ks-frontend-services/src/service/external';
import { useCurrentCompany } from 'hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { downloadBase64PDF } from 'utils';
import type { TermsFields } from '../useTermsAndConditionsForm';

export interface FetchTermsAndConditionsTermsHook {
  isLoading: boolean;
  isRefreshing: boolean;
  isFetching: boolean;
  error?: string;
  contract?: PdfComponent[];
  onClick: () => void;
}

export default function useFetchTermsAndConditionsTerms(
  body: ContractRequestBodyBody | undefined,
): FetchTermsAndConditionsTermsHook {
  const { company } = useCurrentCompany();
  const { setValue } = useFormContext<TermsFields>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const [contract, setContract] = useState<PdfComponent[] | undefined>();

  const { submit: getTerms } = useSubmit(
    services.contractv2.contractterms as unknown as Service<
      ContractTermsResponse,
      object,
      ContractRequestBody
    >,
  );

  const { submit: getPdf } = useSubmit(
    services.contractv2.contractpdf as unknown as Service<
      ContractPdfResponse,
      object,
      ContractRequestBody
    >,
  );

  const { showError, showSuccess } = useToast();

  const name = useMemo(
    () => `Ideal4Finance-T&C-${company?.id ?? ''}.pdf`,
    [company],
  );

  const onClick = useCallback(() => {
    if (!body) return;
    setIsFetching(true);
    getPdf({ body: body as unknown as ContractRequestBody }).then(
      ({ data, error: err }) => {
        setIsFetching(false);
        if (err) {
          showError(`Error encountered: ${err}`);
          return;
        }
        if (!data) {
          showError('Data missing in response - please try again');
          return;
        }
        setValue('document.content', data.base64);
        setValue('document.name', name);
        downloadBase64PDF(data?.base64, name);
        showSuccess('Document downloaded.');
      },
    );
  }, [body, setValue, getPdf, name, showError, showSuccess]);

  const signeeRef = useRef<string | undefined>();
  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (!body) return;
    if (initRef.current && body.signee === signeeRef.current) return;
    initRef.current = true;
    signeeRef.current = body?.signee;
    setIsRefreshing(true);
    getTerms({
      body: body as unknown as ContractRequestBody,
    }).then(({ data, error: err }) => {
      setIsLoading(false);
      setIsRefreshing(false);
      if (err) {
        if (typeof err === 'string') {
          setError(err);
          return;
        }
        setError(`Error: ${err}`);
        return;
      }
      setContract(data?.contract);
    });
  }, [body, contract, getTerms]);

  return {
    isLoading,
    isRefreshing,
    isFetching,
    error,
    contract,
    onClick,
  };
}
